import React, { useEffect, useState } from "react";
import {
  Box, Card, CardContent, Grid, Typography,
  TextField, Button, CircularProgress,
  Select, MenuItem, FormControl, InputLabel, Chip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useSnackbar } from "notistack";
import { useSchool } from "../../Hooks/SchoolContext";
import {
  get_school_tutors_config_api, update_school_tutors_config_api,
  create_school_connect_onboarding_link_api, get_school_connect_status_api,
  create_connect_onboarding_link_api, get_connect_status_api,
} from "../../DAL/SchoolPortal/Tutors";
import { get_school_details_api, update_school_details_api } from "../../DAL/SchoolPortal/SchoolPortal";
import CoursesAndPricing from "./Tutors/CoursesAndPricing";
import InstructorInviteTools from "../../components/InstructorInviteTools";
import InstructorPay from "./Tutors/InstructorPay";
import { platformFee } from "../../utils/platformFee";
import { success as successColor } from "../../layouts/schoolPortal/schoolPortalTheme";
import { currencySymbol, regionOf } from "../../utils/region";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    borderRadius: "9px",
    color: "#1a1a1a",
    "& fieldset": { borderColor: "rgba(26,26,26,0.16)" },
    "&:hover fieldset": { borderColor: "rgba(26,26,26,0.28)" },
    "&.Mui-focused fieldset": { borderColor: "#c01e2e", borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input": { color: "#1a1a1a" },
  "& .MuiInputLabel-root": { color: "#aaa", fontSize: "14px" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#c01e2e" },
};

const sectionCardSx = {
  backgroundColor: "#ffffff",
  border: "1px solid rgba(26,26,26,0.09)",
  borderRadius: "14px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  mb: 3,
};

export default function SchoolSettings() {
  const { modules, isIndependentInstructor } = useSchool();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [schoolData, setSchoolData] = useState({
    name: "", email: "", phone: "", address: "",
    city: "", state: "", zipCode: "", website: "", description: "",
  });

  // ── Tutors config ──────────────────────────────────────────────────────
  const [tutorsConfig, setTutorsConfig] = useState(null);
  const schoolRegion = regionOf(tutorsConfig);
  const [savingTutorsConfig, setSavingTutorsConfig] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [schoolConnectStatus, setSchoolConnectStatus] = useState(null);
  // Independent instructor: their own Stripe Connect account (the same one
  // Teaching uses), not the school-level pooled account.
  const [instructorConnectStatus, setInstructorConnectStatus] = useState(null);

  useEffect(() => {
    fetchSchoolData();
    if (isIndependentInstructor) {
      // The school-level Tutors config (marketplace, payout mode, courses,
      // instructor pay) is an organisation's concern. A one-person school
      // only needs profile and payouts.
      get_connect_status_api().then((res) => { if (res.code === 200) setInstructorConnectStatus(res.data); });
      return;
    }
    if (modules.tutors) {
      get_school_tutors_config_api().then((res) => { if (res.code === 200) setTutorsConfig(res.data); });
      get_school_connect_status_api().then((res) => { if (res.code === 200) setSchoolConnectStatus(res.data); });
    }
  }, [modules.tutors, isIndependentInstructor]); // eslint-disable-line react-hooks/exhaustive-deps

  // One helper, so a fourth hardcoded "15%" cannot be written from memory.
  const fee = platformFee(tutorsConfig);

  const saveTutorsConfig = async (updates) => {
    setSavingTutorsConfig(true);
    const res = await update_school_tutors_config_api(updates);
    if (res.code === 200) {
      setTutorsConfig(res.data);
      enqueueSnackbar("Tutors settings saved", { variant: "success" });
    } else {
      enqueueSnackbar(res.message || "Couldn't save Tutors settings", { variant: "error" });
    }
    setSavingTutorsConfig(false);
  };

  // Both payout flows start on this page, so both come back to it.
  const handleInstructorConnectClick = async () => {
    const res = await create_connect_onboarding_link_api("/school/settings");
    if (res.code === 200 && res.data?.url) {
      window.location.href = res.data.url;
    } else {
      enqueueSnackbar(res.message || "Couldn't start Stripe onboarding", { variant: "error" });
    }
  };

  const handleSchoolConnectClick = async () => {
    const res = await create_school_connect_onboarding_link_api("/school/settings");
    if (res.code === 200 && res.data?.url) {
      window.location.href = res.data.url;
    } else {
      enqueueSnackbar(res.message || "Couldn't start Stripe onboarding", { variant: "error" });
    }
  };

  // Real data, from the school's own record.
  //
  // This was a setTimeout returning "My Flight School" and "123 Aviation Way,
  // Los Angeles" — and handleSave was a setTimeout that fired a success toast
  // and wrote nothing. A school typed their real address, was told it saved,
  // and came back to a placeholder one in California.
  const fetchSchoolData = async () => {
    try {
      const res = await get_school_details_api();
      if (res.code === 200) {
        const d = res.data || {};
        setSchoolData({
          name: d.name || "",
          email: d.contact_email || "",
          phone: d.phone || "",
          website: d.website || "",
          description: d.description || "",
          address: d.address?.street || "",
          city: d.address?.city || "",
          state: d.address?.state || "",
          zipCode: d.address?.postalCode || "",
          country: d.address?.country || "",
        });
      } else {
        setLoadError(res.message || "Could not load your school details");
      }
    } catch {
      setLoadError("Could not load your school details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field) => (e) => setSchoolData((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    if (!schoolData.name?.trim()) {
      enqueueSnackbar(isIndependentInstructor ? "Your group needs a name" : "Your school needs a name", { variant: "error" });
      return;
    }
    setIsSaving(true);
    try {
      const res = await update_school_details_api({
        name: schoolData.name,
        description: schoolData.description,
        contact_email: schoolData.email,
        phone: schoolData.phone,
        website: schoolData.website,
        address: {
          street: schoolData.address,
          city: schoolData.city,
          state: schoolData.state,
          postalCode: schoolData.zipCode,
          country: schoolData.country,
        },
      });
      // Only claim success when the server says so. The whole reason this
      // screen was untrustworthy is that it reported success unconditionally.
      if (res.code === 200) enqueueSnackbar("School details saved", { variant: "success" });
      else enqueueSnackbar(res.message || "Could not save your school details", { variant: "error" });
    } catch {
      enqueueSnackbar("Could not save your school details", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}><CircularProgress sx={{ color: "#c01e2e" }} /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {loadError && (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: "8px", border: "1px solid #b45309", background: "rgba(180,83,9,0.06)" }}>
          <Typography sx={{ fontFamily: "'Rubik', sans-serif", fontSize: "13px", fontWeight: 600, color: "#b45309" }}>
            {loadError}
          </Typography>
        </Box>
      )}

      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "#1a1a1a", fontFamily: "'Rubik', sans-serif" }}>
          {isIndependentInstructor ? "Settings" : "School Settings"}
        </Typography>
        <Button
          variant="contained"
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={isSaving}
          sx={{ backgroundColor: "#c01e2e", textTransform: "none", fontFamily: "'Rubik', sans-serif", borderRadius: "8px", boxShadow: "none", "&:hover": { backgroundColor: "#a81828", boxShadow: "none" } }}
        >
          Save Changes
        </Button>
      </Box>

      {/* Your plan: what this school has switched on, Adapt+ included. Read
          only — the plan is a commercial term set with XAir, not a setting. */}
      {!isIndependentInstructor && (
        <Card sx={sectionCardSx}>
          <CardContent>
            <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#1a1a1a", fontFamily: "'Rubik', sans-serif", mb: 2 }}>
              Your XAir plan
            </Typography>
            {[
              { key: "learn", name: "XAir Learn", what: "Ground school for every student: lessons, reading and unlimited practice." },
              { key: "insights", name: "XAir Insights", what: "Progress, weak areas and alerts for every student, for you and your instructors." },
              { key: "adaptPlus", name: "Adapt+ for every student", what: "Each student's exam estimate, pass chance and earliest exam date, with the same picture on their record for instructors." },
              { key: "tutors", name: "XAir Tutors", what: "Students book and pay for 1-to-1 sessions with your instructors." },
            ].map((m) => {
              const on = m.key === "learn" ? modules?.learn !== false : modules?.[m.key] === true;
              return (
                <Box key={m.key} sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.25, borderTop: "1px solid #F0EDE9" }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontFamily: "'Rubik', sans-serif", fontSize: "14px", fontWeight: 600, color: "#1a1a1a" }}>{m.name}</Typography>
                    <Typography sx={{ fontFamily: "'Rubik', sans-serif", fontSize: "12px", color: "#8a8580" }}>{m.what}</Typography>
                  </Box>
                  <Typography sx={{ fontFamily: "'Rubik', sans-serif", fontSize: "12px", fontWeight: 700, whiteSpace: "nowrap", color: on ? successColor : "#b5b0aa" }}>
                    {on ? (m.key === "adaptPlus" ? "✓ Opted in" : "✓ Included") : "Not on your plan"}
                  </Typography>
                </Box>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Basic Information */}
      <Card sx={sectionCardSx}>
        <CardContent>
          <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#1a1a1a", fontFamily: "'Rubik', sans-serif", mb: 3 }}>
            Basic Information
          </Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}><TextField fullWidth label={isIndependentInstructor ? "Group name" : "School Name"} value={schoolData.name} onChange={handleChange("name")} sx={inputSx} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Email" type="email" value={schoolData.email} onChange={handleChange("email")} sx={inputSx} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Phone" value={schoolData.phone} onChange={handleChange("phone")} sx={inputSx} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Website" value={schoolData.website} onChange={handleChange("website")} sx={inputSx} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Description" multiline rows={3} value={schoolData.description} onChange={handleChange("description")} sx={inputSx} /></Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Address */}
      <Card sx={sectionCardSx}>
        <CardContent>
          <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#1a1a1a", fontFamily: "'Rubik', sans-serif", mb: 3 }}>
            Address
          </Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12}><TextField fullWidth label="Street Address" value={schoolData.address} onChange={handleChange("address")} sx={inputSx} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="City" value={schoolData.city} onChange={handleChange("city")} sx={inputSx} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="State" value={schoolData.state} onChange={handleChange("state")} sx={inputSx} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="ZIP Code" value={schoolData.zipCode} onChange={handleChange("zipCode")} sx={inputSx} /></Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Payouts for an independent instructor: their own Connect account. */}
      {isIndependentInstructor && (
        <Card sx={sectionCardSx}>
          <CardContent>
            <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#1a1a1a", fontFamily: "'Rubik', sans-serif", mb: 0.5 }}>
              Payouts
            </Typography>
            <Typography sx={{ fontSize: "12.5px", color: "#8a8580", fontFamily: "'Rubik', sans-serif", mb: 2 }}>
              Stripe pays you directly when a student books a session with you. Your rate and availability are on the Teaching page.
            </Typography>
            {instructorConnectStatus?.onboardingComplete ? (
              <Chip label="Connected" sx={{ backgroundColor: `${successColor}1a`, color: successColor, fontWeight: 700, fontFamily: "'Rubik', sans-serif" }} />
            ) : (
              <Button
                onClick={handleInstructorConnectClick}
                variant="contained"
                sx={{ backgroundColor: "#c01e2e", textTransform: "none", fontFamily: "'Rubik', sans-serif", borderRadius: "8px", boxShadow: "none", "&:hover": { backgroundColor: "#a81828", boxShadow: "none" } }}
              >
                {instructorConnectStatus?.accountExists ? "Continue Stripe setup" : "Set up payouts"}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* The join link outlives onboarding — this is its permanent home once
          the setup page is behind them. Groups and schools both: a school's
          link is one link for the whole organisation. */}
      <Card sx={sectionCardSx}>
        <CardContent>
          <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#1a1a1a", fontFamily: "'Rubik', sans-serif", mb: 0.5 }}>
            Invite students
          </Typography>
          <Typography sx={{ fontSize: "12.5px", color: "#8a8580", fontFamily: "'Rubik', sans-serif", mb: 2 }}>
            {isIndependentInstructor
              ? "Anyone with your link can join your group and gets 14 days free."
              : "Anyone with this link can join as a student. Each one is counted as a seat, the same as a student you invite by email."}
          </Typography>
          <InstructorInviteTools />
        </CardContent>
      </Card>

      {/* Tutors */}
      {modules.tutors && !isIndependentInstructor && (
        <Card sx={sectionCardSx}>
          <CardContent>
            <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#1a1a1a", fontFamily: "'Rubik', sans-serif", mb: 0.5 }}>
              Tutors
            </Typography>
            <Typography sx={{ fontSize: "12.5px", color: "#8a8580", fontFamily: "'Rubik', sans-serif", mb: 3 }}>
              These control what your instructors and students see on the Tutors page — nothing there works for anyone until you save this once.
            </Typography>

            {!tutorsConfig ? (
              <CircularProgress size={22} sx={{ color: "#c01e2e" }} />
            ) : (
              <>
                {/* What the school actually sells. First, because everything
                    below it is about how a course is sold rather than which
                    ones exist. */}
                <Box sx={{ backgroundColor: "#F5F2EF", border: "1px solid #E8E4DF", borderRadius: "12px", p: 2.5, mb: 3 }}>
                  <Typography sx={{ fontFamily: "'Rubik', sans-serif", fontWeight: 700, fontSize: "14.5px", color: "#1a1a1a", mb: 1.5 }}>
                    Courses you sell
                  </Typography>
                  <CoursesAndPricing />
                </Box>

                <Box sx={{ backgroundColor: "#F5F2EF", border: "1px solid #E8E4DF", borderRadius: "12px", p: 2.5, mb: 3 }}>
                  <Typography sx={{ fontFamily: "'Rubik', sans-serif", fontWeight: 700, fontSize: "14.5px", color: "#1a1a1a", mb: 1.5 }}>
                    Open marketplace
                  </Typography>
                  <Typography sx={{ fontFamily: "'Rubik', sans-serif", fontSize: "13px", color: "#4a4540", lineHeight: 1.6, mb: 1.5 }}>
                    By default, only your own students can book lessons with your own instructors — nobody outside your school
                    sees you, and vice versa. Turning this on opens a two-way door: your instructors become bookable by students
                    at other participating schools and by independent XAir students with no school of their own — giving
                    your school worldwide reach — while your own students can book instructors elsewhere too.
                  </Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                    <Box
                      onClick={() => !savingTutorsConfig && saveTutorsConfig({ allowOpenMarketplace: false })}
                      sx={{
                        p: 1.5, borderRadius: "9px", backgroundColor: "#ffffff", cursor: savingTutorsConfig ? "default" : "pointer",
                        border: `1.5px solid ${!tutorsConfig.allowOpenMarketplace ? "#c01e2e" : "#E8E4DF"}`,
                        boxShadow: !tutorsConfig.allowOpenMarketplace ? "0 0 0 1px #c01e2e" : "none",
                      }}
                    >
                      <Typography sx={{ fontFamily: "'Rubik', sans-serif", fontWeight: 700, fontSize: "12px", color: !tutorsConfig.allowOpenMarketplace ? "#c01e2e" : "#1a1a1a", mb: 0.5 }}>
                        {!tutorsConfig.allowOpenMarketplace ? "✓ " : ""}Off (default)
                      </Typography>
                      <Typography sx={{ fontFamily: "'Rubik', sans-serif", fontSize: "12px", color: "#5a5550", lineHeight: 1.5 }}>In-house only — your own students booking your own instructors. {fee.sentence}</Typography>
                    </Box>
                    <Box
                      onClick={() => !savingTutorsConfig && saveTutorsConfig({ allowOpenMarketplace: true })}
                      sx={{
                        p: 1.5, borderRadius: "9px", backgroundColor: "#ffffff", cursor: savingTutorsConfig ? "default" : "pointer",
                        border: `1.5px solid ${tutorsConfig.allowOpenMarketplace ? "#c01e2e" : "#E8E4DF"}`,
                        boxShadow: tutorsConfig.allowOpenMarketplace ? "0 0 0 1px #c01e2e" : "none",
                      }}
                    >
                      <Typography sx={{ fontFamily: "'Rubik', sans-serif", fontWeight: 700, fontSize: "12px", color: tutorsConfig.allowOpenMarketplace ? "#c01e2e" : "#1a1a1a", mb: 0.5 }}>
                        {tutorsConfig.allowOpenMarketplace ? "✓ " : ""}On
                      </Typography>
                      <Typography sx={{ fontFamily: "'Rubik', sans-serif", fontSize: "12px", color: "#5a5550", lineHeight: 1.5 }}>Also opens cross-school bookings, either direction, plus every independent XAir student worldwide — no school required on their end. {fee.applies ? `The same ${fee.pct === null ? "platform" : fee.pct + "%"} fee applies either direction.` : "There is still no per-booking fee."}</Typography>
                    </Box>
                  </Box>
                </Box>

                <Grid container spacing={2.5} sx={{ mb: 1 }}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth sx={inputSx}>
                      <InputLabel>Payout goes to</InputLabel>
                      <Select value={tutorsConfig.payoutMode || "instructor"} label="Payout goes to" onChange={(e) => saveTutorsConfig({ payoutMode: e.target.value })} disabled={savingTutorsConfig}>
                        <MenuItem value="instructor">Directly to each instructor</MenuItem>
                        <MenuItem value="school">Pooled to the school's own account</MenuItem>
                        <MenuItem value="split">Split between school and instructor</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth sx={inputSx}>
                      <InputLabel>Instructor rates</InputLabel>
                      <Select value={tutorsConfig.rateMode || "org_standard"} label="Instructor rates" onChange={(e) => saveTutorsConfig({ rateMode: e.target.value })} disabled={savingTutorsConfig}>
                        <MenuItem value="org_standard">Everyone charges the school rate</MenuItem>
                        <MenuItem value="admin_set">You set each instructor's rate</MenuItem>
                        <MenuItem value="instructor_set">Instructors set their own rate</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth label={`School default hourly rate (${currencySymbol(schoolRegion)})`} type="number" sx={inputSx}
                      value={tutorsConfig.defaultHourlyRate ?? 50}
                      onChange={(e) => setTutorsConfig((prev) => ({ ...prev, defaultHourlyRate: e.target.value }))}
                      onBlur={(e) => saveTutorsConfig({ defaultHourlyRate: parseFloat(e.target.value) || 0 })}
                      helperText="Fallback for any session length without its own price below"
                    />
                  </Grid>
                </Grid>

                {tutorsConfig.payoutMode === "split" && (
                  <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontFamily: "'Rubik', sans-serif", fontSize: "13px", color: "#5a5550", mb: 1.5 }}>
                      {fee.applies ? "After XAir's fee comes out, what's" : "What's"} left splits between the instructor and your school.
                    </Typography>
                    <TextField
                      label="Instructor's share of what's left (%)" type="number" sx={{ ...inputSx, width: 260 }}
                      value={tutorsConfig.splitInstructorPercent ?? 70}
                      onChange={(e) => setTutorsConfig((prev) => ({ ...prev, splitInstructorPercent: e.target.value }))}
                      onBlur={(e) => {
                        const value = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0));
                        saveTutorsConfig({ splitInstructorPercent: value });
                      }}
                      helperText={`Your school keeps the remaining ${100 - (tutorsConfig.splitInstructorPercent ?? 70)}%`}
                    />
                  </Box>
                )}

                {/* Pay, per session type and per person. The control above is
                    one percentage for the whole school and stays as the
                    fallback for anyone not covered here — a school that never
                    opens this section keeps paying exactly what it paid
                    before. */}
                <InstructorPay
                  tutorsConfig={tutorsConfig}
                  setTutorsConfig={setTutorsConfig}
                  schoolRegion={schoolRegion}
                  feeApplies={fee.applies}
                />

                <Box sx={{ mb: 1 }}>
                  <Typography sx={{ fontFamily: "'Rubik', sans-serif", fontSize: "13px", fontWeight: 600, color: "#1a1a1a", mb: 0.5 }}>Session pricing</Typography>
                  <Typography sx={{ fontFamily: "'Rubik', sans-serif", fontSize: "12.5px", color: "#8a8580", mb: 1.5 }}>
                    Default price per session length — only used while rates are set to "Everyone charges the school rate", or as a fallback for any tier an instructor hasn't priced themselves.
                  </Typography>
                  <Grid container spacing={2}>
                    {[["min10", "10 minutes"], ["min30", "30 minutes"], ["min60", "60 minutes"]].map(([key, label]) => (
                      <Grid item xs={4} key={key}>
                        <TextField
                          fullWidth label={`${label} (${currencySymbol(schoolRegion)})`} type="number" sx={inputSx}
                          value={tutorsConfig.defaultRates?.[key] ?? ""}
                          onChange={(e) => setTutorsConfig((prev) => ({ ...prev, defaultRates: { ...prev.defaultRates, [key]: e.target.value } }))}
                          onBlur={(e) => saveTutorsConfig({ defaultRates: { ...tutorsConfig.defaultRates, [key]: e.target.value ? parseFloat(e.target.value) : undefined } })}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                {(tutorsConfig.payoutMode === "school" || tutorsConfig.payoutMode === "split") && (
                  <Box sx={{ mt: 2.5, pt: 2.5, borderTop: "1px solid rgba(26,26,26,0.09)" }}>
                    <Typography sx={{ fontFamily: "'Rubik', sans-serif", fontSize: "13px", fontWeight: 600, color: "#1a1a1a", mb: 1 }}>School payout account</Typography>
                    {schoolConnectStatus?.onboardingComplete ? (
                      <Chip label="Connected" sx={{ backgroundColor: `${successColor}1a`, color: successColor, fontWeight: 700, fontFamily: "'Rubik', sans-serif" }} />
                    ) : (
                      <Button
                        onClick={handleSchoolConnectClick}
                        variant="contained"
                        sx={{ backgroundColor: "#c01e2e", textTransform: "none", fontFamily: "'Rubik', sans-serif", borderRadius: "8px", boxShadow: "none", "&:hover": { backgroundColor: "#a81828", boxShadow: "none" } }}
                      >
                        Set up school payouts
                      </Button>
                    )}
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
